
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { TRAIT_CLASS_RANGES, Trait } from "@/lib/consts";
import { CompetencyStatus } from "@/lib/competencies";
import { CandidateSummary, STATUS_LABELS, TraitGroupDef } from "./shared";

interface SpreadBubble {
  candidate: CandidateSummary;
  score: number;
  status: CompetencyStatus;
}

interface SpreadCluster {
  items: SpreadBubble[];
  centre: number;
}

// Scores whose whole span is within this are treated as a tie and render as one count dot.
// Close-but-distinct scores are no longer grouped; they render as individual (possibly overlapping) bubbles.
const TIE_EPS = 1.5;

const LEVEL_KEYS: Record<CompetencyStatus, string> = { "highly-prioritized": "hp", prioritized: "p", neutral: "lp" };

@Component
export default class CompetencySpread extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true })
  readonly candidates!: CandidateSummary[];

  @Prop({ required: true })
  readonly groups!: TraitGroupDef[];

  @Prop({ default: false })
  readonly printing!: boolean;

  public openGroups: Record<string, boolean> = { must: true, nice: false, neutral: false };
  public tooltip: { visible: boolean; x: number; y: number; name: string; levelText: string; levelKey: string; rows: Array<{ name: string; color: string; levelText: string; levelKey: string }> } = {
    visible: false,
    x: 0,
    y: 0,
    name: "",
    levelText: "",
    levelKey: "p",
    rows: [],
  };

  isOpen(key: string): boolean {
    return this.printing || !!this.openGroups[key];
  }

  toggleGroup(key: string): void {
    this.$set(this.openGroups, key, !this.openGroups[key]);
  }

  benchLow(trait: string): number {
    return TRAIT_CLASS_RANGES[trait as Trait]?.low ?? 40;
  }

  benchWidth(trait: string): number {
    const range = TRAIT_CLASS_RANGES[trait as Trait];
    return range ? range.high - range.low : 20;
  }

  // Lowest scores first so higher scores render on top when bubbles overlap.
  bubbles(trait: string): SpreadBubble[] {
    return this.candidates
      .map((candidate) => ({
        candidate,
        score: candidate.traitScores[trait] ?? 0,
        status: candidate.traitInfo[trait]?.status ?? ("neutral" as CompetencyStatus),
      }))
      .sort((a, b) => a.score - b.score);
  }

  // Collapses tied scores (span <= TIE_EPS) into one count dot; everything else stays an individual bubble.
  clusters(trait: string): SpreadCluster[] {
    const out: SpreadBubble[][] = [];
    this.bubbles(trait).forEach((item) => {
      const last = out[out.length - 1];
      if (last && item.score - last[0].score <= TIE_EPS) last.push(item);
      else out.push([item]);
    });
    return out.map((items) => ({
      items,
      centre: items.reduce((sum, x) => sum + x.score, 0) / items.length,
    }));
  }

  showTooltip(event: MouseEvent, item: SpreadBubble): void {
    this.tooltip = {
      visible: true,
      x: event.clientX + 14,
      y: event.clientY - 10,
      name: item.candidate.name,
      levelText: STATUS_LABELS[item.status],
      levelKey: LEVEL_KEYS[item.status],
      rows: [],
    };
  }

  showGroupTooltip(event: MouseEvent, cluster: SpreadCluster): void {
    this.tooltip = {
      visible: true,
      x: event.clientX + 14,
      y: event.clientY - 10,
      name: `Tied · ${cluster.items.length} candidates`,
      levelText: "",
      levelKey: "p",
      rows: cluster.items.map((item) => ({ name: item.candidate.name, color: item.candidate.color, levelText: STATUS_LABELS[item.status], levelKey: LEVEL_KEYS[item.status] })),
    };
  }

  moveTooltip(event: MouseEvent): void {
    this.tooltip.x = event.clientX + 14;
    this.tooltip.y = event.clientY - 10;
  }

  hideTooltip(): void {
    this.tooltip.visible = false;
  }
}
